import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { cancel, later } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { pickNextRotationIndex } from 'webapp/utils/random-rotation';

import themed from 'webapp/decorators/themed';
import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';

const TYPE_SPEED_MS = 45;
const ERASE_SPEED_MS = 25;
const HOLD_MS = 20000;
// wait for enter animation to finish
const START_DELAY_MS = 400;

type RotationPhase = 'typing' | 'erasing';

interface Args {
	placeholder?: string;
	rotatingPlaceholders?: string[];
	maxlength?: number;
	theme?: string;
	hideLogo?: boolean;
	onSubmit: (value: string) => void;
}

@themed()
export default class CommonGenieInputComponent extends Component<Args> {
	@tracked value = '';
	@tracked private displayedPlaceholder = '';

	declare themeClass: string; // from @themed

	private rotationTimer?: ReturnType<typeof later>;
	private rotationIndex = 0;
	private rotationPhase: RotationPhase = 'typing';
	private rotationCharPos = 0;

	get buttonClassNames(): string {
		const classes = ['genie-input'];

		if (this.args.hideLogo) {
			classes.push('genie-input--no-logo');
		}

		classes.push(this.themeClass);

		return classes.join(' ');
	}

	get isSubmitDisabled(): boolean {
		return !this.value;
	}

	get maxlength(): number {
		return this.args.maxlength ?? 2000;
	}

	get isRotatingPlaceholder(): boolean {
		return (this.args.rotatingPlaceholders?.length ?? 0) > 0;
	}

	get activePlaceholder(): string {
		if (this.isRotatingPlaceholder) {
			return this.displayedPlaceholder;
		}

		return this.args.placeholder ?? '';
	}

	@action
	startPlaceholderRotation(): void {
		const phrases = this.args.rotatingPlaceholders;
		if (!phrases?.length) {
			return;
		}

		if (this.rotationTimer) {
			cancel(this.rotationTimer);
		}

		this.rotationIndex = Math.floor(Math.random() * phrases.length);
		this.rotationPhase = 'typing';
		this.rotationCharPos = 0;
		this.rotationTimer = later(this, this.tickPlaceholder, START_DELAY_MS);
	}

	private tickPlaceholder(): void {
		const phrases = this.args.rotatingPlaceholders;
		if (!phrases?.length) {
			return;
		}

		const phrase = phrases[this.rotationIndex] ?? '';
		let delay = TYPE_SPEED_MS;

		if (this.rotationPhase === 'typing') {
			this.rotationCharPos += 1;
			this.displayedPlaceholder = phrase.slice(0, this.rotationCharPos);

			if (this.rotationCharPos >= phrase.length) {
				this.rotationPhase = 'erasing';
				delay = HOLD_MS;
			}
		} else {
			this.rotationCharPos -= 1;
			this.displayedPlaceholder = phrase.slice(0, Math.max(this.rotationCharPos, 0));

			if (this.rotationCharPos <= 0) {
				this.rotationIndex = pickNextRotationIndex(phrases.length, this.rotationIndex);
				this.rotationPhase = 'typing';
			} else {
				delay = ERASE_SPEED_MS;
			}
		}

		this.rotationTimer = later(this, this.tickPlaceholder, delay);
	}

	@action
	submit(): void {
		if (this.isSubmitDisabled) {
			return;
		}

		this.args.onSubmit(this.value);
	}

	override willDestroy(): void {
		super.willDestroy();

		if (this.rotationTimer) {
			cancel(this.rotationTimer);
		}
	}

	<template>
		<div class={{this.buttonClassNames}} {{didInsert this.startPlaceholderRotation}}>
			{{#unless @hideLogo}}
				<CommonUiIconComponent @name="Genie" @size="lg" @gradient="orange-violet" />
			{{/unless}}
			<FormInputComponent
				name="prompt"
				placeholder={{this.activePlaceholder}}
				maxlength={{this.maxlength}}
				@value={{this.value}}
				@hideFloatLabel={{true}}
				@insertNewline={{this.submit}}
			/>
			<a
				class="genie-input__submit
					{{if this.isSubmitDisabled 'genie-input__submit--disabled'}}"
				{{on "click" this.submit}}
			>
				<CommonUiIconComponent @name="ArrowUp" @size="md" />
			</a>
		</div>
	</template>
}
